import type { BasePageProps, MenuItem } from "@/types";
import {
  Box,
  Typography,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import ReplayIcon from "@mui/icons-material/Replay";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/router";

import AnnouncementDialog from "@/components/common/AnnouncementDialog";
import { PLACEHOLDER } from "@/components/common/SafeImage";
import PageShell from "@/components/common/PageShell";
import CatalogSkeleton from "@/components/menu/CatalogSkeleton";
import CategoryTabs, {
  type CategoryTabOption,
} from "@/components/menu/CategoryTabs";
import ContactFabs from "@/components/menu/ContactFabs";
import MainSlider from "@/components/menu/MainSlider";
import SlateHomeNavbar from "@/components/menu/SlateHomeNavbar";
import MenuItemGrid from "@/components/menu/MenuItemGrid";
import HomeNavbar from "@/components/menu/HomeNavbar";
import OrderAgain from "@/components/menu/OrderAgain";
import PopularItems from "@/components/menu/PopularItems";
import RecentOrdersRail from "@/components/menu/RecentOrdersRail";
import StoreStatusBanner from "@/components/menu/StoreStatusBanner";
import OfflineOrdersBanner from "@/components/common/OfflineOrdersBanner";
import OrderBar from "@/components/common/OrderBar";
import SiteFooter from "@/components/common/SiteFooter";
import CartPanel from "@/components/common/CartPanel";
import { ORDER_TYPE_ENUM, platformKey } from "@/constants/enums";
import {
  CATALOG_LOAD_FAILED_MESSAGE,
  ENTER_DELIVERY_INFORMATION_MESSAGE,
} from "@/constants/messages";
import { APPBAR_HEIGHT, PAGE_PADDING } from "@/constants/layout";
import useDesignType from "@/hooks/useDesignType";
import useStoreStatus from "@/hooks/useStoreStatus";
import useWalletSync from "@/hooks/useWalletSync";
import { useAppDispatch, useAppSelector } from "@/store";
import {
  openDrawer,
  openModal,
  setRunStoreStatusCheck,
} from "@/store/slices/commonSlice";
import { DRAWER_VIEW, MODAL_VIEW } from "@/constants/views";
import { loadMenu, setSelectedCategory } from "@/store/slices/menuSlice";
import { loadOffers } from "@/store/slices/offersSlice";
import { loadVouchers } from "@/store/slices/vouchersSlice";
import { loadGiftCards } from "@/store/slices/giftCardsSlice";
import { setCloseDates } from "@/store/slices/closeDatesSlice";
import { setSettings } from "@/store/slices/settingsSlice";
import { setShifts } from "@/store/slices/shiftsSlice";
import { setSnackbar } from "@/store/slices/snackbarSlice";
import { findItemBySlug, itemPath } from "@/lib/itemSlug";

/**
 * `MenuBody` + `CartPanel` — the two components module 9802 and module 91849
 * export, and the WHOLE of what both menu routes render.
 *
 * The original's `/menu` and `/menu/dine-in` pages are the same six lines of
 * JSX: a `PageShell` around `<MenuBody/>` and `<CartPanel/>`, plus the
 * announcement dialog. Everything that differs between them — which order type
 * the basket takes, which data keys are fetched, whether a floor and table come
 * off the query string — lives in the PAGE, not here. This component is that
 * shared body, so the dine-in route is the original's copy of the menu rather
 * than a second menu.
 *
 * The page's `<head>` is NOT rendered here. `_app` owns it, outside
 * `PersistGate`, keyed on `router.pathname` through `PAGE_TITLES` — which is
 * where the original puts it and the only place it can run during SSR.
 */
export type MenuScreenProps = BasePageProps;

export default function MenuScreen({ data }: MenuScreenProps) {
  const dispatch = useAppDispatch();
  const router = useRouter();

  const { categories, items, error, selectedCategoryId, search } =
    useAppSelector((state) => state.menu);
  const settings = useAppSelector((state) => state.settings.data);
  const orderType = useAppSelector((state) => state.cart.orderType);
  const cartLines = useAppSelector((state) => state.cart.items);
  const deliveryAddress = useAppSelector((state) => state.cart.address);
  const openModalView = useAppSelector((state) => state.common.modal);
  const isStaff = useAppSelector((state) => state.auth.isStaff);
  const isLoggedIn = useAppSelector((state) => state.auth.isLoggedIn);
  const theme = useTheme();
  const isLargerThanMobile = useMediaQuery(theme.breakpoints.up("lg"));
  // Mirrors the selected category so the observer can skip redundant dispatches.
  const selectedCategoryRef = useRef<string | null>(null);
  const [announcementOpen, setAnnouncementOpen] = useState(false);
  // The menu column is its own scroll container — see the observer below.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [scrollDirection, setScrollDirection] = useState<"up" | "down">("up");

  // The original paints the trading status for the CURRENT order type as a
  // full-width band under the top bar — "🕓 Open now" in green while the
  // kitchen is taking orders.
  const storeStatus = useStoreStatus();
  const platformStatus = storeStatus[platformKey(orderType)];

  // Module 93057 — both menu routes call it directly. It consumes the
  // `?refresh_customer=1` the order page sends them back with and polls the
  // customer's balances until they settle.
  useWalletSync();

  useEffect(() => {
    if (!data) return;
    dispatch(setSettings(data.settings ?? null));
    dispatch(setCloseDates(data.closeDates ?? []));
    dispatch(setShifts(data.shifts ?? []));
    dispatch(setRunStoreStatusCheck(true));
    // The original opens the announcement once the menu payload lands, and
    // never for staff.
    if (data.settings?.announcement_message && !isStaff)
      setAnnouncementOpen(true);
  }, [data, dispatch, isStaff]);

  useEffect(() => {
    if (!settings?._id) return;
    dispatch(loadMenu({ tenantId: settings._id, orderType }));
    // The wallet's three catalogues. The original receives all three over its
    // POS socket; the storefront has none, so they load here with the menu.
    dispatch(loadOffers(settings._id));
    dispatch(loadVouchers(settings._id));
    dispatch(loadGiftCards(settings._id));
  }, [dispatch, settings?._id, orderType]);

  // Every category that has something to show, each with its own items.
  const sections = useMemo(() => {
    const term = search.trim().toLowerCase();
    const itemsByCategory = new Map<string, MenuItem[]>();
    for (const item of items) {
      if (term && !item.name?.toLowerCase().includes(term)) continue;
      const bucket = itemsByCategory.get(item.category_id) ?? [];
      bucket.push(item);
      itemsByCategory.set(item.category_id, bucket);
    }
    return categories.flatMap((category) => {
      const categoryItems = itemsByCategory.get(category._id);
      return categoryItems?.length ? [{ ...category, items: categoryItems }] : [];
    });
  }, [categories, items, search]);

  /**
   * `settings.web_design_type` has SIX members — CLASSIC, CLASSIC_VIBRANT,
   * MODERN, MODERN_SLATE, MODERN_VIBRANT, MODERN_SLATE_VIBRANT — and every
   * consumer derives its own flags from the string rather than reading a
   * context or the theme (the design type never touches the palette). Anything
   * missing or unknown behaves as CLASSIC. See `useDesignType`.
   */
  const design = useDesignType();
  const isModernDesign = design.isModernDesign;
  const sliderImages = settings?.web_slider_images ?? [];
  const menuCover =
    /images%2Fcommon%2Fbrand-cover\.svg/i.test(
      settings?.web_cover_image || "",
    ) &&
    !/butt\s+karahi/i.test(
      settings?.public_business_name || settings?.name || "",
    )
      ? PLACEHOLDER.cover
      : settings?.web_cover_image;

  /**
   * The three SYNTHETIC tabs, and the two flags that gate them.
   *
   * `rI` holds `hasRecentOrders` / `hasPopularItems` as local state and lets the
   * rails themselves raise them, because only a rail knows whether its own data
   * arrived. The pairing is verbatim: `hasRecentOrders` publishes **two** tabs
   * (Recent Orders AND Order Again, off the one flag), `hasPopularItems`
   * publishes one, and they carry ICONS where a real category carries an
   * `image_url`.
   *
   * They are not categories and have no items — they are the two order rails
   * and the featured rail, addressed as if they were sections, which is why the
   * strip has to be index-based rather than keyed on a category id.
   */
  const [hasRecentOrders, setHasRecentOrders] = useState(false);
  const [hasPopularItems, setHasPopularItems] = useState(false);

  const syntheticTabs = useMemo<CategoryTabOption[]>(() => {
    const list: CategoryTabOption[] = [];
    if (hasRecentOrders) {
      list.push(
        { _id: "recent-orders", name: "Recent Orders", icon: <HistoryIcon /> },
        { _id: "order-again", name: "Order Again", icon: <ReplayIcon /> },
      );
    }
    if (hasPopularItems) {
      list.push({
        _id: "popular-items",
        name: "Popular Items",
        icon: <LocalFireDepartmentIcon />,
      });
    }
    return list;
  }, [hasRecentOrders, hasPopularItems]);

  /**
   * Tab labels are `${category.name} (${category.items.length})` — built here,
   * as `rI` builds them (symbol `R`) — behind whichever synthetic tabs are
   * live. The synthetic ones keep their bare names: the count suffix is applied
   * to catalog categories only.
   */
  const tabOptions = useMemo<CategoryTabOption[]>(
    () => [
      ...syntheticTabs,
      ...sections.map((section) => ({
        _id: section._id,
        name: `${section.name} (${section.items.length})`,
        image_url: section.image_url,
      })),
    ],
    [syntheticTabs, sections],
  );

  const tabIndex = useMemo(() => {
    const found = tabOptions.findIndex(
      (option) => option._id === selectedCategoryId,
    );
    return found >= 0 ? found : 0;
  }, [tabOptions, selectedCategoryId]);

  const handleTabChange = useCallback((index: number) => {
    const option = tabOptions[index];
    if (!option) return;
    dispatch(setSelectedCategory(option._id));
    document
      .getElementById(`category-${option._id}`)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [dispatch, tabOptions]);

  /** Basket quantity per item id, so a tile's Fab can show its count. */
  const quantities = useMemo(() => {
    const map: Record<string, number | undefined> = {};
    for (const line of cartLines) {
      map[line.itemId] = (map[line.itemId] ?? 0) + line.quantity;
    }
    return map;
  }, [cartLines]);

  /** `a` — the basket's total selected items, summed over the lines. */
  const totalSelectedItems = useMemo(
    () => cartLines.reduce((sum, line) => sum + line.quantity, 0),
    [cartLines],
  );

  /**
   * **The delivery prompt.** `iF`'s own mount effect, verbatim:
   *
   * ```js
   * useEffect(() => {
   *   let e = setTimeout(() => {
   *     (f(getCategoryState(orderCategories)),
   *      PLATFORMS_BY_ORDER_TYPE[ORDER_TYPE_ENUM.DELIVERY].includes(v) &&
   *        !w.current && a > 0 && null == j && !Z &&
   *        (d(setShowModal(DELIVERY_INFO_MODAL)),
   *         d(setSnackbar({ message: "Enter delivery information",
   *                         anchorOrigin: { vertical: "top", horizontal: "center" },
   *                         severity: "warning" })),
   *         (w.current = !0)));
   *   }, 0);
   *   return () => clearTimeout(e);
   * }, [a, v]);
   * ```
   *
   * `v` is the cart's platform, `a` the total selected items, `j` its
   * `delivery_details`, `Z` is `state.modal.open` and `w` is a ref that fires
   * the prompt **once per mount**.
   *
   * This is how a customer is asked for their location, and it was the only
   * opener missing from this tree: the landing page's Delivery card and the
   * cart's Delivery tab both only set the fulfilment method, and the original's
   * tab handler (module `99916`) opens nothing either — it writes the platform
   * and rewrites `?order_type=`. Adding the first item to a delivery basket is
   * what raises the modal, and a basket that already has items raises it on the
   * next load.
   *
   * The deps are the original's two on purpose: including `deliveryAddress` or
   * the open modal would re-arm the prompt every time the customer dismissed
   * it, which is exactly what the `w` ref exists to prevent.
   */
  const deliveryPromptedRef = useRef(false);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (
        orderType === ORDER_TYPE_ENUM.DELIVERY &&
        !deliveryPromptedRef.current &&
        totalSelectedItems > 0 &&
        deliveryAddress == null &&
        !openModalView
      ) {
        dispatch(openModal(MODAL_VIEW.DELIVERY_INFO));
        dispatch(
          setSnackbar({
            message: ENTER_DELIVERY_INFORMATION_MESSAGE,
            anchorOrigin: { vertical: "top", horizontal: "center" },
            severity: "warning",
          }),
        );
        deliveryPromptedRef.current = true;
      }
    }, 0);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalSelectedItems, orderType]);

  /** Every item opens its detail drawer so instructions, quantity and the
   * favourite control are available consistently, even without modifiers. */
  const handleItemClick = useCallback((item: MenuItem) => {
    const { slug: _slug, ...query } = router.query;
    // MUI's modal manager has to isolate a very large menu tree when the drawer
    // opens. Doing that synchronously in the click handler made the measured
    // handler exceed 400 ms. The card's native ripple paints immediately;
    // commit the drawer in the first idle slice so the input can finish and
    // paint that feedback before the heavier modal work begins.
    const openItem = () => {
      dispatch(openDrawer({ view: DRAWER_VIEW.ITEM_OPTIONS, payload: item }));
      const updateUrl = () => {
        void router.push({ pathname: itemPath(item), query }, undefined, {
          shallow: true,
          scroll: false,
        });
      };
      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(updateUrl, { timeout: 750 });
      } else {
        setTimeout(updateUrl, 0);
      }
    };
    // Two frames are intentional: the first callback is before the feedback
    // paint; scheduling from it puts the drawer commit after that paint. A
    // single rAF (and Chrome's eager idle callback) can still be charged to the
    // originating interaction on slower phones.
    requestAnimationFrame(() => requestAnimationFrame(openItem));
  }, [dispatch, router]);

  // A copied/search-engine item URL opens the same fully validated modifier
  // flow as a tile click once the catalogue has loaded.
  useEffect(() => {
    const slug = Array.isArray(router.query.slug)
      ? router.query.slug[0]
      : router.query.slug;
    if (!slug || !items.length) return;
    const item = findItemBySlug(items, slug);
    if (item) dispatch(openDrawer({ view: DRAWER_VIEW.ITEM_OPTIONS, payload: item }));
  }, [dispatch, items, router.query.slug]);

  /**
   * A catalog request that FAILED is not a tenant with no items, and the
   * skeleton screen below cannot tell you which you are looking at — the
   * original genuinely cannot either, because its loader `eu` swallows
   * everything but an expired token.
   *
   * This raises the failure through the original's OWN channel and with the
   * original's OWN copy: the snackbar of module 35474, error severity,
   * top-centre anchor, and the string its address flow uses for exactly this
   * case ("An error occured. …", the original's spelling). Recorded in
   * RECOVERY_PLAN as a deliberate deviation.
   */
  useEffect(() => {
    if (!error) return;
    dispatch(
      setSnackbar({
        message: CATALOG_LOAD_FAILED_MESSAGE,
        severity: "error",
        anchorOrigin: { vertical: "top", horizontal: "center" },
      }),
    );
  }, [error, dispatch]);

  // The original hides its top bar while you scroll DOWN and brings it back on
  // the first upward scroll (`top: -60` ⇄ `top: 0`, 0.6s), with a 5px dead zone
  // and an 80px grace at the top of the list. Reproduced here off the same
  // container's `scrollTop`.
  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return undefined;
    let last = 200;
    let primed = false;
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = window.setTimeout(() => {
        frame = 0;
        const offset = node.scrollTop;
        if (Math.abs(offset - last) > 5) {
          setScrollDirection(offset > last && offset > 80 ? "down" : "up");
        }
        if (primed) last = offset;
        else primed = true;
      }, 100);
    };
    node.addEventListener("scroll", onScroll);
    return () => {
      node.removeEventListener("scroll", onScroll);
      if (frame) window.clearTimeout(frame);
    };
  }, []);

  // Scrolling the menu must move the tab highlight with it — the strip is the
  // only indication of where you are in a long continuous menu. Observes each
  // section and selects the top-most one currently in view.
  useEffect(() => {
    if (!sections.length || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (!visible) return;
        const id = visible.target.id.replace("category-", "");
        // Guard: setting the same value on every scroll frame would thrash the store.
        if (id && id !== selectedCategoryRef.current) {
          selectedCategoryRef.current = id;
          dispatch(setSelectedCategory(id));
        }
      },
      {
        // The menu scrolls INSIDE its own column, exactly as the original does
        // (its scroll-direction hook reads `ref.current.scrollTop`, not the
        // window's), so the observer has to watch that element — against the
        // viewport it would never fire.
        root: scrollRef.current,
        // Trip just below the sticky header + tab strip, so the highlighted tab
        // is the section actually under them.
        rootMargin: `-${APPBAR_HEIGHT + 60}px 0px -55% 0px`,
        threshold: 0,
      },
    );
    // Every TAB's section, not just the catalog ones — the three synthetic
    // sections are addressed the same way, so leaving them unobserved would
    // strand the highlight on the first category while you scroll past them.
    tabOptions.forEach((option) => {
      const node = document.getElementById(`category-${option._id}`);
      if (node) observer.observe(node);
    });
    return () => observer.disconnect();
  }, [sections, tabOptions, dispatch]);

  return (
    <>
      <PageShell
        // The reference does NOT cap the menu: at a 1378px window its cart
        // column sits at x=965, i.e. content 0–965 + cart 965–1378 with no
        // max-width. Any cap leaves dead space at the sides on wide screens.
        maxWidth={false}
        // The original's menu page has no app bar: `PageShell` there is only a
        // row container, and the bar you see is `HomeNavbar`, rendered inside
        // the left column so the cart panel starts at y:0 beside it.
        hideBar
        disableBodyPadding
      >
        {/* The original's menu is a ROW of two full-height columns, and the
            LEFT one owns the scrolling — its scroll-direction hook reads
            `ref.current.scrollTop`, not the window's. Scrolling the document
            instead is what made the sticky layers unreliable: `_app`'s
            `<main>` is `overflow: hidden; height: 100%`, so a page that relies
            on document scroll has no scroll container for `top:` to resolve
            against. */}
        <Stack
          direction="row"
          spacing={0}
          alignItems="stretch"
          sx={{ overflow: "hidden", height: "100%", flex: 1 }}
        >
        <Stack
          flex={1}
          minWidth={0}
          sx={{ overflow: "hidden", backgroundColor: "background.paper" }}
        >
          <Box
            ref={scrollRef}
            sx={{
              flex: 1,
              overflowY: "auto",
              // The category strip bleeds 15px into the column's padding on
              // each side; without this the scroller grows a horizontal
              // scrollbar and steals 15px of the viewport height.
              overflowX: "hidden",
              position: "relative",
              scrollBehavior: "smooth",
            }}
          >
          {/* `isSlateDesign ? <nd/> : <tE/>` — the ONLY navbar switch. Note it
              is `includes("SLATE")`, so `MODERN_SLATE_VIBRANT` gets the Slate
              bar even though its TAB items come from the Vibrant branch. */}
          {design.isSlateDesign ? (
            <SlateHomeNavbar
              showFirstOptionOnly={!isLargerThanMobile}
              scrollDirection={scrollDirection}
              isModernDesign={design.isModernDesign}
              isVibrantDesign={design.isVibrantDesign}
            />
          ) : (
            <HomeNavbar
              showFirstOptionOnly={!isLargerThanMobile}
              scrollDirection={scrollDirection}
              isModernDesign={design.isModernDesign}
              isVibrantDesign={design.isVibrantDesign}
            />
          )}
          <StoreStatusBanner
            status={platformStatus?.status}
            message={platformStatus?.message}
          />
          {/* PRODUCT CHANGE §2 — the offline notice, directly under the store
              status band because they answer the same question ("can I order
              right now?") and must not appear in two different places.
              Renders NOTHING while the connection is up and nothing is
              queued. */}
          <OfflineOrdersBanner />
          {/* `web_slider_images` — `isModernVibrantDesign ? <ny/> : <nf/>`,
              and a bare `Stack spacing={1.5}` when the tenant has none. */}
          <MainSlider
            images={sliderImages}
            // The gallery is empty on this tenant (a CONFIGURATION gap —
            // Settings ▸ Store Setup ▸ Branding ▸ Website Visuals ▸ Image
            // Gallery). `web_cover_image` IS set and is the same shape, so the
            // hero shows one real banner instead of a hero-shaped hole. See
            // the note in `MainSlider`.
            fallbackImage={menuCover}
            isModernDesign={design.isModernDesign}
            isModernVibrantDesign={design.isModernVibrantDesign}
            isLargerThanMobile={isLargerThanMobile}
          />
          {/* `nt` — the tab strip. It sits in the HEADER block in the original
              (`n_`), i.e. ABOVE the recent-orders rail, which lives in the
              content block (`rT`). It also carries its own gutters, so it
              belongs outside the padded catalog column. */}
          <CategoryTabs
            options={tabOptions}
            tabIndex={tabIndex}
            onTabChange={handleTabChange}
            scrollDirection={scrollDirection}
            flags={design}
          />
          {/* The two ORDER rails. They carry the wide-screen gutters
              themselves, exactly as `nQ`/`rS` do, so nesting them inside the
              15px padding would double them. Both are gated together on
              `!isStaff && isLoggedIn`, as `rI` gates them — a staff member
              taking an order for a walk-in customer must not see the tablet
              owner's history. */}
          {!isStaff && isLoggedIn && (
            <>
              <RecentOrdersRail
                id="category-recent-orders"
                setHasRecentOrders={setHasRecentOrders}
              />
              <OrderAgain
                id="category-order-again"
                quantities={quantities}
                symbol={settings?.currency_symbol}
                code={settings?.currency_code}
                isModernDesign={isModernDesign}
                onItemClick={handleItemClick}
              />
            </>
          )}
          {/* `j && <ri/>` — the featured rail is the tenant's own opt-in, and
              unlike the order rails it is shown to everyone, signed in or not. */}
          {settings?.web_show_featured_items && (
            <PopularItems
              id="category-popular-items"
              featuredCategoryName={settings?.featured_category_name}
              quantities={quantities}
              symbol={settings?.currency_symbol}
              code={settings?.currency_code}
              isModernDesign={isModernDesign}
              onItemClick={handleItemClick}
              setHasPopularItems={setHasPopularItems}
            />
          )}
          {/* THE CONTAINMENT CONTEXT for the item grid's column ladder.
              `MenuItemGrid` counts columns off THIS element's width rather
              than the viewport's, because the permanent basket takes 360–500px
              out of the window under `CLASSIC*` and a viewport-keyed ladder
              would put five columns in a four-column space. Named `menu` so
              the queries read as `@container menu (min-width: …)`. */}
          <Stack
            spacing={2}
            sx={{
              px: `${PAGE_PADDING}px`,
              pt: 2,
              pb: 4,
              containerType: "inline-size",
              containerName: "menu",
            }}
          >
          {/* `z = 0 === i.length ? <nS/> : …` — the original renders its
              catalog SKELETON while nothing has arrived, and nothing else. It
              has no "nothing to show" panel, no retry button and no error
              screen on this route, so the invented EmptyState that used to
              live here (and told a customer to set an env var) is gone. The
              failure itself is surfaced through the original's own snackbar —
              see the effect above. */}
          {sections.length === 0 && (
            <CatalogSkeleton isModernDesign={isModernDesign} />
          )}

          {/* The reference storefront (smashd-dha.eposmatic.com/menu) renders
              EVERY category in one continuous scroll — a section heading per
              category followed by its tiles — rather than tab-switching to show
              one category at a time. The tabs scroll you to a section; they do
              not filter. */}
          {sections.map((section) => (
            <Box
              key={section._id}
              id={`category-${section._id}`}
              // Clears the sticky header + tab strip when scrolled to.
              sx={{
                scrollMarginTop: `${APPBAR_HEIGHT + 56}px`,
                // Browser-native list virtualization: preserve every item in
                // the DOM for SEO, search and scroll anchors, but skip style,
                // layout and paint work for categories far outside the
                // viewport. `auto` remembers the measured size after a section
                // has been visited, preventing later category jumps.
                contentVisibility: "auto",
                containIntrinsicSize: "auto 560px",
              }}
            >
              {section.banner_url && (
                <Box
                  component="img"
                  src={section.banner_url}
                  alt={section.name}
                  sx={{ width: "100%", borderRadius: 2, display: "block", objectFit: "cover", mb: 1.5 }}
                />
              )}
              <Typography component="h2" variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
                {section.name}
              </Typography>
              {/* One of THREE grids, chosen by this category's own
                  `display_type` — REGULAR, CLASSIC or SIMPLE. It is a
                  per-category setting, so a single menu mixes all three, and
                  `isModernDesign` (from `settings.web_design_type`) layers on
                  top to change the column counts and gaps. */}
              <MenuItemGrid
                items={section.items}
                displayType={section.display_type}
                isModernDesign={isModernDesign}
                quantities={quantities}
                onClick={handleItemClick}
                symbol={settings?.currency_symbol}
                code={settings?.currency_code}
              />
            </Box>
          ))}
          </Stack>
          <SiteFooter />
          {/* `q` — inside the scroller, after the footer, exactly as `iF`
              places it. It owns its own breakpoint and design-type switch:
              below `lg` the sticky "View Order" bar, above it the floating
              "View Cart" pill for `MODERN*` and nothing at all for
              `CLASSIC*`. Do NOT re-wrap it in a `display` breakpoint — the
              desktop pill lives in this branch too. */}
          <OrderBar />
          </Box>
          {/* `rN` — the floating WhatsApp + Call pair. It is a sibling of the
              scroll port, not a child of it, so it stays put while the catalog
              moves. It renders nothing unless the tenant publishes a phone
              number, and never for staff or a dine-in basket. */}
          <ContactFabs />
        </Stack>
        {/* The basket is a Drawer and picks its own variant: permanent (in the
            flow, right) for `CLASSIC*` from `lg` up, temporary everywhere
            else. It is a sibling of the menu column, as in the original, where
            `r.Z` sits beside `n.Z` inside the page shell. */}
        <CartPanel />
        </Stack>
        <AnnouncementDialog
          open={announcementOpen}
          onClose={() => setAnnouncementOpen(false)}
        />
      </PageShell>
    </>
  );
}
